import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { client } from "./api";

const EditModeCtx = createContext({
  editMode: false,
  toggleEditMode: () => {},
  setEditMode: () => {},
  labels: {},
  labelsLoaded: false,
  getLabel: (_k, fallback) => fallback,
  saveLabel: async () => {},
});

const LS_KEY = "op:edit-mode";

export const EditModeProvider = ({ children }) => {
  const [editMode, setEditModeState] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(LS_KEY) === "1";
  });
  const [labels, setLabels] = useState({});
  const [labelsLoaded, setLabelsLoaded] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, editMode ? "1" : "0"); } catch {}
  }, [editMode]);

  // Load labels on mount (and after login by watching token via storage event)
  const loadLabels = useCallback(async () => {
    try {
      const { data } = await client.get("/ui/labels");
      setLabels(data || {});
    } catch {
      setLabels({});
    } finally {
      setLabelsLoaded(true);
    }
  }, []);

  useEffect(() => { loadLabels(); }, [loadLabels]);

  const getLabel = useCallback(
    (key, fallback) => (labels[key] != null && labels[key] !== "" ? labels[key] : fallback),
    [labels]
  );

  const saveLabel = useCallback(async (key, value) => {
    // Optimistic
    setLabels((prev) => {
      const next = { ...prev };
      if (value == null || value === "") delete next[key];
      else next[key] = value;
      return next;
    });
    try {
      await client.put(`/ui/labels/${encodeURIComponent(key)}`, { value });
    } catch {
      // Revert on failure by reloading
      loadLabels();
    }
  }, [loadLabels]);

  const toggleEditMode = useCallback(() => setEditModeState((v) => !v), []);

  return (
    <EditModeCtx.Provider
      value={{
        editMode,
        toggleEditMode,
        setEditMode: setEditModeState,
        labels,
        labelsLoaded,
        getLabel,
        saveLabel,
      }}
    >
      {children}
    </EditModeCtx.Provider>
  );
};

export const useEditMode = () => useContext(EditModeCtx);
